<template>
  <v-container fluid>
    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Bull Finder'"
      v-bind:icon="'highlight'"
      v-bind:actionLink="'Manage Queries'"
      v-bind:actionLinkHidden="isLoading || !isUserQueries || isSaleContext"
      v-bind:actionButtonHidden="isLoading || !isUserQueries"
      v-bind:buttonIcon="'favorite'"
      v-bind:buttonIconColor="'pink lighten-2'"
      v-bind:pageLogoUrl="'https://cattlytics.blob.core.windows.net/assets/BullfinderLogo.png'"
      @action-button-click="actionButtonClicked"
      @action-link-click="actionLinkClicked"
    />

    <!-- Loading -->
    <template v-if="isLoading && !isAdminy">
      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />
    </template>

    <!-- No Queries Exist -->
    <template v-if="!isLoading && !isUserQueries">
      <v-alert color="info" type="info" border="top" colored-border prominent>
        <v-row justify="center">
          <v-col class="grow">
            <v-row justify="center">
              <span class="title">
                You have no queries defined
              </span>
            </v-row>
            <v-row justify="center">
              <span class="subtitle-1">
                Go to <i>Query Manager</i> to get started
              </span>
            </v-row>
          </v-col>
          <v-col class="shrink">
            <v-btn to="/bullFinderManager" dark>Query Manager</v-btn>
          </v-col>
        </v-row>
      </v-alert>
    </template>

    <v-row no-gutters>

      <!-- List of Queries -->
      <v-col cols="12" xs="12" sm="12" md="5">
        <v-expansion-panels v-model="panel">
          <v-expansion-panel>

            <!-- Expansion Header -->
            <v-expansion-panel-header :color="appColor_Medium" class="elevation-4 py-0">
              <span class="title font-weight-regular white--text">
                {{ queriesTitle }}
              </span>

              <template v-slot:actions>
                <v-icon large color="white">arrow_drop_down</v-icon>
              </template>
            </v-expansion-panel-header>

            <!-- Expansion Content -->
            <v-expansion-panel-content>

              <!-- Customer Filter -->
              <bb-customer-dropdown
                v-if="isAdminy"
                v-model="userId"
                v-bind:profile="profile"
                v-bind:withQueryOnly="true"
                @load="customersLoaded"
                @change="loadQueries()"
                @error="customerError"
                class="mb-2"
              />

              <!-- Category Filter -->
              <v-select
                v-if="categories.length > 0"
                :dense="isSmallDevice"
                :items="categories"
                v-model="selectedCategory"
                @change="activateQuery(pageQueries[0])"
                hide-details
                solo
                >
              </v-select>

              <!-- Query List -->
              <v-list
                v-if="isUserQueries && !isLoading"
                :class="'pt-0' + (pageQueries.length > pageLength ? ' pb-1' : ' pb-0')"
                >
                <template v-for="query in pageQueries">
                  <v-card
                    :key="query.id"
                    :color="query.animalSource.colourCode + ' lighten-4'"
                    @click="activateQuery(query)"
                    class="mx-0 my-1 elevation-0"
                    >
                    <user-query
                      v-bind:scope="scope"
                      v-bind:query="query"
                      v-bind:activeQuery="activeQuery"
                      v-bind:userFilters="query.userQueryDef.userFilters"
                      v-bind:colourCode="query.animalSource.colourCode"
                      v-bind:chipPrimaryTrait="query.userQueryDef.userTraits[0].description"
                      v-bind:chipAnimalSourceCode="query.animalSource.cosmosCode"
                      v-bind:isEditQueryClicked="activeQuery.id === query.id && isEditQueryClicked"
                      @change="refreshAnimals(true)"
                      @cancel="isEditQueryClicked = false"
                    />
                  </v-card>
                </template>
              </v-list>

              <!-- Pagination -->
              <template v-if="showPagination">
                <v-row justify="center">
                  <v-pagination
                    v-model="page"
                    :length="pageLength"
                    class="mt-2 mb-4"
                  />
                </v-row>
              </template>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>

      <!-- Search Results -->
      <v-col cols="12" xs="12" sm="12" md="7">
        <v-card class="pa-0" style="background-color: transparent">
          <!-- BB Selections Drawer -->
          <bb-selections-drawer
            v-bind:selections="drawSelections"
            v-bind:activeBreed="activeBreed"
            v-bind:animalSource="activeAnimalSource"
            v-bind:selectionsDrawer="selectionsDrawer"
            @close="selectionsDrawer = false"
          />

          <v-data-table
            v-if="queryResults.length > 0"
            :headers="tableHeaders"
            :items="activeResult.qualifiedBulls"
            :loading="activeResult.isRefreshing"
            :search="search"
            :no-data-text="noDataText"
            mobile-breakpoint="500"
            :class="searchResultsClass + ' ' + activeQueryBackgroundColor"
            >
            <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

            <!-- Header Toolbar -->
            <template v-slot:top>
              <v-app-bar dense dark :class="activeQueryDarkColor">
                <template v-if="saleHasLiveUpdates">
                  <!-- Hamburger for BB Current Selections drawer-->
                  <v-app-bar-nav-icon
                    v-if="!isLoadingSelections"
                    @click.stop="showLiveSelections"
                    :disabled="isLoadingSelections"
                    >
                  </v-app-bar-nav-icon>
                  <v-btn v-else loading disabled icon></v-btn>
                </template>

                <!-- Query Title -->
                <v-toolbar-title :class="isSmallDevice ? 'body-2 pl-2' : 'pl-2'">
                  {{ activeQueryTitle }}
                </v-toolbar-title>

                <v-spacer />

                <!-- Percentile Error Button/Menu -->
                <template v-if="!activeResult.isRefreshing">
                  <v-menu
                    v-if="activeResult.pctErrMessage !== null"
                    bottom
                    offset-y
                    >
                    <template v-slot:activator="{ on }">
                      <v-btn outlined v-on="on" class="mr-2">
                        <v-icon color="red">
                          priority_high
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-card width="400">
                      <v-card-text class="body-1">
                        An error occurred getting EBV percentile limits
                        <router-link to="/bullFinder">
                          <span @click="refreshAnimals(true)"
                            >Click here to try again.</span
                            >
                        </router-link>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </template>

                <!-- Refresh Button -->
                <template v-if="showRefreshButton">
                  <v-switch
                    v-if="isAdmin"
                    v-model="isAutoRefresh"
                    value
                    @change="autoRefreshChanged"
                    :disabled="activeResult.isRefreshing || activeResult.isRefreshingSaleStatus"
                    hide-details
                    >
                    <span slot="label" class="caption white--text">
                      Auto-refresh
                    </span>
                  </v-switch>

                  <v-btn
                    :small="isSmallDevice"
                    outlined
                    @click="updateSaleStatus"
                    :loading="activeResult.isRefreshing || activeResult.isRefreshingSaleStatus"
                    :disabled="activeResult.isRefreshing || activeResult.isRefreshingSaleStatus"
                    class="mx-2"
                    >
                    <v-icon :small="isSmallDevice">refresh</v-icon>
                    <span slot="loader" class="custom-loader">
                      <v-icon :small="isSmallDevice">cached</v-icon>
                    </span>
                  </v-btn>
                </template>

                <!-- Query Settings Button -->
                <v-btn
                  outlined
                  :disabled="activeResult.isRefreshing"
                  :small="isSmallDevice"
                  @click="isEditQueryClicked = true"
                  class="mr-2"
                  >
                  <v-icon :small="isSmallDevice">settings</v-icon>
                </v-btn>

                <!-- Export Button -->
                <v-btn
                  :small="isSmallDevice"
                  :disabled="activeResult.isRefreshing || isExportDisabled"
                  outlined
                  @click="exportDialog = true"
                  >
                  <v-icon :small="isSmallDevice">file_download</v-icon>
                </v-btn>
              </v-app-bar>

              <!-- Filter Toolbar -->
              <v-toolbar flat class="mt-2" :color="activeQueryBackgroundColor">
                <!-- Search Filter -->
                <v-text-field
                  :dense="isSmallDevice"
                  dark
                  v-model="search"
                  append-icon="search"
                  label="Filter..."
                  single-line
                  clearable
                  solo-inverted
                  hide-details
                  flat
                  :class="activeQueryFilterColor"
                />
              </v-toolbar>
            </template>

            <!-- VID / breeder info popup -->
            <template v-slot:item.bull.vid="{ item }">
              <bb-ranch-menu
                v-bind:vid="item.bull.vid"
                v-bind:ranch="getBeefboosterRanch(item.bull.breederAccountNo)"
              />
            </template>

            <!-- Rank Button Menu -->
            <template v-slot:item.bull.rank="{ item }">
              <animal-rank-menu
                v-bind:rank="item.bull.rank"
                v-bind:rankColor="getRankColor(item.sequencedMatches)"
                v-bind:displayValues="item.bullDisplayValues"
                v-bind:criteriaTraits="item.bullCriteriaTraits"
                v-bind:criterionDescriptions="activeResult.criterionDescriptions"
              />
            </template>

            <!-- Sale Class Chip -->
            <!-- <template v-slot:item.bull.saleClass="{ item }">
              <v-chip dark small :color="getSaleClassColor(item.bull.saleClass)">
                {{ item.bull.saleClass }}
              </v-chip>
            </template> -->

            <template v-slot:item.bull.price="{ item }">
              {{ getMoneyValue(item.bull.price) }}
            </template>

            <!-- Sale Status Chip -->
            <template v-slot:item.bull.saleStatus="{ item }">
              <v-chip dark small :color="getStatusColor(item.bull.saleStatus)">
                {{ item.bull.saleStatus }}
              </v-chip>
            </template>

            <!-- Picture -->
            <template v-slot:item.bull.isPicture="{ item }">
              <router-link v-if="item.bull.isPicture" to='/bullFinder' style="text-decoration: none">
                <span @click="showPicture(item.bull)">
                  <v-icon>
                    photo_camera
                  </v-icon>
                </span>
              </router-link>
              <v-icon v-else color="grey lighten-2">
                photo_camera
              </v-icon>
            </template>

            <!-- Favorite -->
            <template v-slot:item.bull.favoriteBullId="{ item }">
              <!-- <v-badge overlap small color="blue-grey darken-1"> -->
              <!-- <span slot="badge">{{ item.bull.favoriteCount }}</span> -->
              <span class="pl-2">
                <router-link
                  to='/bullFinder'
                  v-if="item.bull.favoriteBullId === 0"
                  style="text-decoration: none"
                  >
                  <span @click="toggleFavorite(item.bull)">
                    <v-icon color="grey lighten-1">
                      favorite_border
                    </v-icon>
                  </span>
                </router-link>
                <router-link v-else to='/bullFinder' style="text-decoration: none">
                  <span @click="toggleFavorite(item.bull)">
                    <v-icon color="pink lighten-1">
                      favorite
                    </v-icon>
                  </span>
                </router-link>
              </span>
              <!-- </v-badge> -->
            </template>

            <!-- Detail -->
            <template v-slot:item.details="{ item }">
              <v-btn
                icon
                class="ma-0 pa-0"
                @click="showBullDetail(item.bull, item.sequencedMatches)"
                :loading="item.bull.id === activeBullId && isLoadingBullDetail"
                >
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Picture Display Dialog -->
    <v-dialog v-model="pictureDialog" max-width="600px" persistent>
      <v-card color="black" class="ma-0">
        <v-toolbar dark flat color="black" class="elevation-0" dense>
          <template v-if="!isLoadingPicture">
            <v-btn text @click="closePicture()" icon><v-icon>close</v-icon></v-btn>
            <v-toolbar-title>{{ pictureTitle }}</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items v-if="isFavorite">
              <v-icon color="pink lighten-1" class="ml-3">favorite</v-icon>
            </v-toolbar-items>
          </template>
        </v-toolbar>
        <v-card-text class="pb-2 pr-2 pl-2 pt-0">
          <div v-if="isLoadingPicture" class="text-center mb-4">
            <v-progress-circular :size="40" :width="3" color="grey lighten-1" indeterminate />
          </div>
          <v-img v-else :src="pictureBase64" max-height="600" style="display:block" contain></v-img>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bull Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="220px" right>
      <bull-detail
        v-bind:animalSource="activeAnimalSource"
        v-bind:bullDetail="bullDetail"
        v-bind:params="bullDetailParams"
      />
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        v-bind:errorTitle="errorTitle"
        v-bind:errorText="errorText"
        v-on:close="errorDialog = false"
      />
    </v-dialog>

    <!-- Export Dialog -->
    <v-dialog v-model="exportDialog" persistent max-width="250px">
      <app-export
        v-model="isExporting"
        v-bind:title="'Export Top 20'"
        @ok="downloadExport"
        @cancel="exportDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import AppError from '@/components/AppError'
import AppExport from '@/components/AppExport'
import UserQuery from '@/components/UserQuery'
import AnimalRankMenu from '@/components/AnimalRankMenu'
import BbCustomerDropdown from '@/components/BbCustomerDropdown'
import BbRanchMenu from '@/components/BbRanchMenu'
import BbSelectionsDrawer from '@/components/BbSelectionsDrawer'
import BullDetail from '@/components/BullDetail'

import { mapGetters } from 'vuex'
import {
  AppColor,
  CriterionTypeId,
  InputTypeId,
  FilterName,
  AnimalSourceId
} from '@/config.js'

export default {
  name: 'BullFinder',
  components: {
    AppPageTitle,
    AppError,
    AppExport,
    UserQuery,
    AnimalRankMenu,
    BbCustomerDropdown,
    BbRanchMenu,
    BbSelectionsDrawer,
    BullDetail
  },
  data() {
    return {
      // colour constants
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Background: AppColor.Background,

      // input types
      inputTypeId_RangeSlider: InputTypeId.RangeSlider,
      inputTypeId_MultiSelect: InputTypeId.MultiSelect,

      // criterion types
      criterionTypeIdFilter: CriterionTypeId.Filter,
      criterionTypeIdTrait: CriterionTypeId.Trait,

      // filter names
      filterName_Breed: FilterName.Breed,
      filterName_BirthYear: FilterName.BirthYear,

      // animal source id
      animalSourceId_Beefbooster: AnimalSourceId.Beefbooster,

      timer: null,
      currentYear: new Date().getFullYear(),
      isAutoRefresh: true,

      scope: 'bullfinder',
      isLoading: true,
      panel: 0,
      queries: [],
      categories: [],
      beefboosterRanches: [],
      selectedCategory: null,
      queryResults: [],
      search: '',
      userTraits: [],
      userQueryId: null,
      userFilters: [],
      page: 1,
      pageSize: 4,
      criterionDescriptions: [],
      filterCriteria: [],
      traitCriteria: [],
      traitIds: [],

      // current selections
      selectionsDrawer: false,
      isLoadingSelections: false,
      activeBullId: null,
      drawSelections: [],

      // bull detail
      detailDialog: false,
      isLoadingBullDetail: false,
      bullDetail: [],
      bullDetailParams: {
        vid: null,
        tag: null,
        rank: null
      },

      // bull picture
      pictureTitle: null,
      pictureBase64: null,
      isFavorite: false,
      pictureDialog: false,
      isLoadingPicture: false,

      // Adminy only
      customers: [],
      userId: null,

      // edit query
      isEditQueryClicked: false,

      // export
      exportDialog: false,
      isExporting: false,

      // error handling
      isError: false,
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    ...mapGetters(['profile', 'currentBullSale', 'appSettings']),
    noDataText () {
      return this.activeResult.isRefreshing ? 'Searching...' : 'No matches'
    },
    isAdmin () {
      if (this.isNullOrUndefined(this.profile)) return false
      return !this.isNullOrUndefined(this.profile.roles)
        ? this.profile.roles.includes('Admin')
        : false
    },
    isBreeder () {
      if (this.isNullOrUndefined(this.profile)) return false
      return !this.isNullOrUndefined(this.profile.roles)
        ? this.profile.roles.includes('Breeder')
        : false
    },
    isAdminy () {
      return this.isAdmin || this.isBreeder
    },
    tableHeaders () {
      if (this.activeResult.isRefreshing) return
      var headers = this.activeResult.tableHeaders
      if (headers.length !== 0) {
        headers = headers.map(c => {
          return {
            text: c.text,
            value: c.value,
            width: this.headerWidth(c),
            align: this.headerAlign(c)
          }
        })
      }

      // ---- add a column for launching the bull detail dialog ----

      // calculate its width
      const allWidths = headers.map(h => {
        return parseInt(h.width.replace('%', ''))
      })
      const totalWidth = allWidths.reduce((a, b) => a + b, 0)

      // add it to headers
      headers.push({
        value: 'details',
        width: totalWidth < 100 ? `${100 - totalWidth}%` : '1%',
        align: 'center'
      })

      return headers
    },
    queriesTitle () {
      return this.isAdminy
        ? 'Customer Queries'
        : this.isSaleContext
        ? this.saleName
        : 'Your Queries'
    },
    selectedCustomer () {
      const userId = this.userId
      const customers = this.customers.filter(u => {
        return u.value === userId
      })
      return customers.length > 0 ? customers[0] : null
    },
    activeAnimalSource () {
      if (this.isNullOrUndefined(this.activeQuery)) return null
      return !this.isNullOrUndefined(this.activeQuery.animalSource)
        ? this.activeQuery.animalSource
        : null
    },
    activeBirthYear () {
      if (this.isNullOrUndefined(this.activeQuery)) return null
      const yob = this.filterName_BirthYear
      const yobFilters = this.activeQuery.userQueryDef.userFilters.filter(f => {
        return f.columnName === yob
      })
      return yobFilters.length > 0 ? parseInt(yobFilters[0].displayValue) : null
    },
    activeBreed () {
      if (this.isNullOrUndefined(this.activeQuery)) return null
      const breed = this.filterName_Breed
      const breedFilters = this.activeQuery.userQueryDef.userFilters.filter(
        f => {
          return f.propertyPath === breed
        }
      )
      return breedFilters.length > 0 ? breedFilters[0].displayValue : null
    },
    isUserQueries () {
      return this.queries.length > 0 || this.isAdminy
    },
    categoryQueries () {
      const c = this.selectedCategory
      return this.queries.filter(q => {
        return q.category === c
      })
    },
    pageQueries () {
      const beginId = (this.page - 1) * this.pageSize
      const endId = beginId + this.pageSize + 1
      const queries = this.categoryQueries.filter(q => {
        return q.index > beginId && q.index < endId
      })
      return queries
    },
    activeQuery () {
      if (this.queries === null) return null
      const activeQueries = this.queries.filter(q => {
        return q.active
      })
      return activeQueries.length > 0 ? activeQueries[0] : null
    },
    activeResult () {
      if (this.queries === null) return null
      const id = this.activeQuery.id
      const results = this.queryResults.filter(r => {
        return r.queryId === id
      })
      return results.length > 0 ? results[0] : null
    },
    activeQueryTitle () {
      return this.activeQuery !== null ? this.activeQuery.title : ''
    },
    pageLength () {
      const numQueries = this.categoryQueries.length
      return numQueries > this.pageSize
        ? Math.ceil(numQueries / this.pageSize)
        : 0
    },
    showPagination () {
      return this.categoryQueries.length > this.pageSize
    },
    isExportDisabled () {
      return false // for now
      // if (this.isNullOrUndefined(this.profile.bbCustomerStatus)) return true
      // return this.isCurrentUser
      //   ? !this.profile.bbCustomerStatus.isValid
      //   : false
    },
    isCurrentUser () {
      return this.profile.id === this.userId
    },
    isSmallDevice () {
      return (
        this.$vuetify.breakpoint.name === 'xs' ||
        this.$vuetify.breakpoint.name === 'sm'
      )
    },

    // sale day
    showRefreshButton () {
      const sourceId =
        this.activeAnimalSource !== null ? this.activeAnimalSource.id : -1
      const yob = this.currentYear - 1
      const isAdminUpdates =
        this.isAdmin &&
        this.appSettings.isSaleDayMode &&
        sourceId === this.animalSourceId_Beefbooster &&
        this.activeBirthYear === yob
      return this.saleHasLiveUpdates || isAdminUpdates
    },
    saleHasLiveUpdates () {
      if (!this.isSaleContext) return false
      return !this.isNullOrUndefined(this.currentBullSale)
        ? this.currentBullSale.isLiveUpdate &&
            this.activeBirthYear === this.saleYob
        : false
    },
    saleName () {
      return !this.isNullOrUndefined(this.currentBullSale)
        ? this.currentBullSale.name
        : null
    },
    saleYob () {
      return !this.isNullOrUndefined(this.currentBullSale)
        ? this.currentBullSale.yob
        : null
    },
    isBreederDay () {
      return !this.isNullOrUndefined(this.currentBullSale)
        ? this.currentBullSale.isBreederDay
        : false
    },
    isSaleContext () {
      return this.$route.query.saleId !== undefined
    },
    // computed css classes/colors
    searchResultsClass () {
      const padding = 'pa-0'
      return this.isSmallDevice
        ? padding + ' mt-2 ' + this.activeQueryBackgroundColor
        : padding + ' ml-2 ' + this.activeQueryBackgroundColor
    },
    activeQueryDarkColor () {
      return `${this.activeQuery.animalSource.colourCode} ${'darken-4'}`
    },
    activeQueryBackgroundColor () {
      return `${this.activeQuery.animalSource.colourCode} ${'lighten-5'}`
    },
    activeQueryFilterColor () {
      return `${this.activeQuery.animalSource.colourCode} ${'lighten-1'}`
    },
    favoriteIconClass () {
      return this.isSmallDevice ? 'mr-0 mb-0' : 'mr-1'
    }
  },
  methods: {
    loadData () {
      if (this.profile.id === undefined) this.$router.push('/')
      this.isLoading = true
      this.userId = this.profile.id

      if (!this.isAdminy) {
        this.loadQueries()
      } else {
        // default user_id might have been passed in
        if (this.isAdminy && this.$route.params.user_id !== undefined) {
          this.userId = this.$route.params.user_id
        }
      }
    },

    loadQueries () {
      const self = this

      const saleId = self.$route.query.saleId
      self.clearTimer()
      appService
        .getBullfinderUserQueries(self.userId, saleId)
        .then(data => {
          if (data.errorSeverity > 0) {
            self.isError = true
            self.errorText = data.errorMessage
            self.errorDialog = true
          } else {
            self.queries = data.queries
            self.categories = data.categories
            self.beefboosterRanches = data.beefboosterRanches
            self.queryResults = self.getIndexedCategoryQueries()
            self.activateDefaultQuery(data.defaultUserQueryId)
          }
          self.isLoading = false
        })
        .catch(() => {
          self.isLoading = false
        })
    },

    activateDefaultQuery (queryId) {
      const defaultQueries = this.queries.filter(q => q.id === queryId)
      const defaultQuery = defaultQueries.length > 0
        ? defaultQueries[0]
        : null
      const isDefault = defaultQuery !== null

      this.selectedCategory = isDefault
        ? defaultQuery.category
        : this.categories[0]

      this.page = isDefault
        ? this.getDefaultPage(queryId)
        : 1
     
      this.refreshAnimals()
    },

    getDefaultPage (queryId) {
      var page = 1
      const index = this.categoryQueries.findIndex(q => q.id === queryId)
      const queryIndex = index + 1 // make it 1-based
      if (queryIndex > this.pageSize) {
        page = Math.floor(queryIndex / this.pageSize) + 1
      }
      return page
    },

    headerWidth (item) {
      const col = item.value.replace('bull.', '')
      switch (col) {
        case 'rank':
          return '12%'

        case 'vid':
          return '12%'

        case 'tag':
          return '10%'

        case 'sire':
          return '12%'

        case 'saleStatus':
          return '17%'

        case 'favoriteBullId':
          return '2%'

        case 'isPicture':
          return '2%'

        default:
          return '15%'
      }
    },

    headerAlign (item) {
      switch (item.text) {
        case 'Rank':
          return 'center'

        default:
          return 'left'
      }
    },

    toggleFavorite (bull) {
      if (bull.favoriteBullId === 0) {
        const year = this.activeBirthYear
        appService
          .addFavoriteBull(
            this.userId,
            bull.altRef,
            this.activeQuery.animalSource.id,
            year
          )
          .then(id => {
            bull.favoriteBullId = id
            bull.favoriteCount++
          })
      } else {
        appService.deleteFavoriteBull(bull.favoriteBullId).then(() => {
          bull.favoriteBullId = 0
          if (bull.favoriteCount > 0) bull.favoriteCount--
        })
      }
    },

    activateQuery (query) {
      const self = this
      if (self.activeQuery.id === query.id) return
      self.queries.forEach(q => {
        q.active = q.id === query.id
      })
      self.refreshAnimals()
    },

    refreshAnimals (force = false) {
      if (this.activeQuery === null) return

      const self = this
      const query = self.activeQuery
      self.isError = false
      self.isEditQueryClicked = false
      const activeResult = self.activeResult
      self.clearTimer()

      if (
        (activeResult.qualifiedBulls.length === 0 || force) &&
        !activeResult.isRefreshing
      ) {
        activeResult.isRefreshing = true
        activeResult.qualifiedBulls = []
        const criteria = self.getSearchCriteria()
        appService.queryBulls(self.userId, query.id, criteria).then(data => {
          if (data.errorSeverity > 0) {
            self.isError = true
            self.errorText = data.errorMessage
            self.errorDialog = true
          } else {
            // get the query result for the right query
            var matchingResults = self.queryResults.filter(function(value) {
              return value.queryId === data.queryId
            })
            if (matchingResults.length > 0) {
              // update the matching query result with the matching bulls
              matchingResults[0].qualifiedBulls = data.qualifiedBulls
              matchingResults[0].criterionDescriptions =
                data.criterionDescriptions
              matchingResults[0].tableHeaders = data.tableHeaders
              matchingResults[0].pctErrMessage = data.errorMessage
            }
            if (self.showRefreshButton) {
              self.updateSaleStatus().then(() => {
                self.setTimer()
              })
            }
          }
          activeResult.isRefreshing = false
        })
      } else {
        self.setTimer()
      }
    },

    updateSaleStatus () {
      if (this.activeQuery === null) return
      if (this.activeResult.isRefreshingSaleStatus) return

      return new Promise((resolve, reject) => {
        const self = this
        self.isError = false
        self.isEditQueryClicked = false

        //---------------------
        const isTestMode = false // uses previous year data
        //---------------------

        const isBreederDay = null // get for all sales
        const breed = self.activeBreed

        self.activeResult.isRefreshingSaleStatus = true
        appService
          .getSaleDaySelections(breed, isBreederDay, isTestMode)
          .then(data => {
            if (data.errorSeverity > 0) {
              self.isError = true
              self.errorText = data.errorMessage
              self.errorDialog = true
              reject(data.errorMessage)
            } else {
              // create array of sold calfSns
              var calfSns = data.selections
                .map(s => {
                  if (s.calfSn !== null) {
                    return parseInt(s.calfSn)
                  }
                })
                .filter(sn => {
                  return sn !== null
                })

              // filter all available bulls in the array
              const availableBulls = self.activeResult.qualifiedBulls.filter(
                b => {
                  return (
                    calfSns.includes(parseInt(b.bull.altRef)) &&
                    b.bull.saleStatus !== 'Sold'
                  )
                }
              )

              // update sale status to SOLD
              availableBulls.forEach(b => {
                const idx1 = self.activeResult.qualifiedBulls.indexOf(b)
                self.activeResult.qualifiedBulls[idx1].bull.saleStatus = 'Sold'
              })

              // filter all sold bulls that have since become available
              const unSoldBulls = self.activeResult.qualifiedBulls.filter(b => {
                return (
                  !calfSns.includes(parseInt(b.bull.altRef)) &&
                  b.bull.saleStatus === 'Sold'
                )
              })

              // update sale status to NULL
              unSoldBulls.forEach(b => {
                const idx2 = self.activeResult.qualifiedBulls.indexOf(b)
                self.activeResult.qualifiedBulls[idx2].bull.saleStatus = null
              })
            }
            self.activeResult.isRefreshingSaleStatus = false
            resolve()
          })
          .catch(() => {
            reject()
          })
      })
    },

    downloadExport ({ exportType }) {
      const self = this

      self.isExporting = true
      const bulls = self.activeResult.qualifiedBulls
      const query = self.activeQuery
      var bullContainers = []

      bulls.forEach(b => {
        bullContainers.push({
          bullId: b.bull.id,
          rank: b.bull.rank,
          sequencedMatches: b.sequencedMatches,
          isFavorite: b.bull.favoriteBullId > 0,
          bullCriteriaTraits: b.bullCriteriaTraits
        })
      })

      const accountNo = self.isAdminy
        ? self.selectedCustomer.accountNo
        : self.profile.bbCustomerStatus.accountNo

      const exportCriteria = {
        userId: self.isAdminy ? self.userId : self.profile.id,
        queryId: self.activeQuery.id,
        accountNo: accountNo,
        animalSourceId: self.activeQuery.animalSource.id,
        criteria: self.getSearchCriteria(),
        fileType: exportType,
        bulls: bullContainers
      }

      const contactName = self.isAdminy
        ? self.selectedCustomer.contactName
        : self.profile.bbCustomerStatus.contactName

      appService
        .getBullFinderExport(
          exportCriteria,
          'Cattlytics Bull Finder - ' +
            contactName +
            ' - ' +
            (query.category.length === 0 ? '' : query.category) +
            ' - ' +
            query.title
        )
        .then(() => {
          self.isExporting = false
          self.exportDialog = false
        })
    },

    getSearchCriteria () {
      var self = this
      var criteriaModels = []

      // filter criteria
      var filters = self.activeQuery.userQueryDef.userFilters
      for (var key1 in filters) {
        criteriaModels.push({
          criterionId: filters[key1].filterId,
          criterionTypeId: self.criterionTypeIdFilter,
          propertyPath: filters[key1].propertyPath,
          dataTypeId: filters[key1].dataTypeId,
          minValue: filters[key1].value,
          maxValue: filters[key1].value
        })
      }

      // trait criteria
      var traits = self.activeQuery.userQueryDef.userTraits
      var seq = 1
      for (var key2 in traits) {
        criteriaModels.push({
          criterionId: traits[key2].traitId,
          criterionTypeId: self.criterionTypeIdTrait,
          propertyPath: traits[key2].propertyPath,
          trait: traits[key2].description,
          searchTypeId: traits[key2].searchTypeId,
          dataTypeId: traits[key2].dataTypeId,
          unit:
            traits[key2].group === 'EBV' || traits[key2].group === 'EPD'
              ? ''
              : traits[key2].unitAbbreviation,
          minValue:
            traits[key2].inputTypeId === self.inputTypeId_RangeSlider
              ? traits[key2].minMaxValue[0].toString()
              : traits[key2].inputTypeId !== self.inputTypeId_MultiSelect
              ? traits[key2].value
              : traits[key2].multiSelectValues.join(),
          maxValue:
            traits[key2].inputTypeId === self.inputTypeId_RangeSlider
              ? traits[key2].minMaxValue[1].toString()
              : traits[key2].inputTypeId !== self.inputTypeId_MultiSelect
              ? traits[key2].value
              : traits[key2].multiSelectValues.join(),
          sequence: seq++
        })
      }
      return criteriaModels
    },

    getStatusColor (saleStatus) {
      if (saleStatus === null) return 'grey lighten-5'
      return saleStatus ? 'red darken-3' : 'transparent'
    },

    getBeefboosterRanch (accountNo) {
      const ranches = this.beefboosterRanches.filter(r => {
        return r.accountNo === accountNo
      })
      return ranches.length > 0 ? ranches[0] : null
    },

    showLiveSelections () {
      const self = this
      const breed = self.activeBreed

      //---------------------
      const isTestMode = false
      //---------------------

      const isBreederDay = self.isBreederDay
      self.isLoadingSelections = true
      appService
        .getSaleDaySelections(breed, isBreederDay, isTestMode)
        .then(data => {
          if (data.errorSeverity > 0) {
            self.isError = true
            self.errorText = data.errorMessage
            self.errorDialog = true
          } else {
            self.drawSelections = data.selections

            // --- simulate a sale in progress
            //  self.drawSelections.forEach(s => { if (s.selectionNum > 3) { s.calfSn = null }})
            // --- temporary - simulate a sale in progress

            self.selectionsDrawer = true
          }
          self.isLoadingSelections = false
        })
        .catch(() => {
          self.isLoadingSelections = false
        })
    },

    findPropertyValue (obj, propertyPath, defaultValue) {
      if (typeof defaultValue == 'undefined') defaultValue = null
      propertyPath = propertyPath.split('.')
      for (var i = 0; i < propertyPath.length; i++) {
        if (typeof obj[propertyPath[i]] == 'undefined') return defaultValue
        obj = obj[propertyPath[i]]
      }
      return obj
    },

    showPicture (item) {
      const self = this
      const vid = item.vid
      const isFavorite = item.favoriteBullId !== 0
      const sourceId = self.activeQuery.animalSource.id

      self.isLoadingPicture = true
      self.pictureDialog = true
      appService.getBullPictureBase64(sourceId, vid).then(data => {
        self.pictureTitle = vid
        self.isFavorite = isFavorite
        self.pictureBase64 = data
        self.isLoadingPicture = false
      })
    },

    closePicture () {
      this.pictureDialog = false
      this.pictureTitle = null
      this.isFavorite = false
    },

    showBullDetail (bull, matches) {
      const self = this

      self.bullDetailParams = {
        vid: bull.vid,
        tag: bull.tag,
        rank: bull.rank,
        rankColor: self.getRankColor(matches)
      }
      self.bullDetail = []
      self.isLoadingBullDetail = true
      self.activeBullId = bull.id

      appService
        .getBullDetail(
          self.activeQuery.animalSource.id,
          self.activeBirthYear,
          bull.id
        )
        .then(data => {
          if (data.errorSeverity > 0) {
            self.isError = true
            self.errorTitle = 'Error Loading Bull Detail'
            self.errorText = data.errorMessage
            self.errorDialog = true
          } else {
            data.headers.forEach(hdr => {
              self.bullDetail.push({
                label: hdr.label,
                type: hdr.dataType,
                value: self.findPropertyValue(
                  data.bullDoc,
                  hdr.propertyPath,
                  ''
                )
              })
            })
            self.detailDialog = true
          }
          self.isLoadingBullDetail = false
          self.activeBullId = null
        })
        .catch(() => {
          self.isLoadingBullDetail = false
        })
    },

    getRankColor (matches) {
      const numCriteria = this.activeQuery.userQueryDef.userTraits.length
      // perfect
      if (matches === numCriteria) {
        return 'green'
        // average
      } else if (matches === numCriteria - 1) {
        return 'yellow'
        // poor
      } else if (matches > 0 && matches <= numCriteria - 2) {
        return 'red'
        // not a match
      } else return 'grey'
    },

    isNullOrUndefined (obj) {
      return obj === undefined || typeof obj === 'undefined' || obj === null
    },

    getIndexedCategoryQueries () {
      const self = this
      var results = []
      self.categories.forEach(c => {
        var idx = 1
        self.queries
          .filter(q => {
            return q.category === c
          })
          .forEach(q => {
            q.index = idx++
            results.push({
              queryId: q.id,
              qualifiedBulls: [],
              tableHeaders: [],
              criterionDescriptions: null,
              isRefreshing: false,
              isRefreshingSaleStatus: false,
              pctErrMessage: null
            })
          })
      })
      return results
    },

    getMoneyValue (value) {
      return value !== null ? `$${value}` : null
    },

    setTimer () {
      if (!this.showRefreshButton) return
      if (!this.isAutoRefresh) return
      this.timer = setInterval(this.updateSaleStatus, 15000)
    },

    clearTimer () {
      if (!this.showRefreshButton) return
      clearInterval(this.timer)
      this.timer = null
    },

    // event handlers
    customersLoaded ({ customers }) {
      this.customers = customers

      // if the params user_id exists in the customer dropdown then set it as default
      if (this.$route.params.user_id !== undefined) {
        const userId = this.$route.params.user_id
        this.userId =
          this.customers.filter(c => {
            return c.value === userId
          }).length > 0
            ? this.$route.params.user_id
            : this.profile.id
      }

      this.loadQueries()
    },

    customerChanged () {
      this.loadQueries()
    },

    customerError ({ error }) {
      if (error.severity > 0) {
        this.isError = true
        this.errorTitle = 'Customer Load Error'
        this.errorText = error.message
        this.errorDialog = true
      }
    },

    actionLinkClicked () {
      const path = this.isCurrentUser
        ? '/bullFinderManager'
        : `/bullFinderManager/${this.userId}`

      if (this.isSaleContext) {
        this.$router
          .push({
            path: path,
            query: {
              saleId: this.currentBullSale.id
            }
          })
          .catch(() => {})
      } else {
        this.$router.push({ path: path }).catch(() => {})
      }
    },

    actionButtonClicked () {
      const path = this.isCurrentUser
        ? '/favoriteBulls'
        : `/favoriteBulls/${this.userId}`

      if (this.isSaleContext) {
        this.$router
          .push({
            path: path,
            query: {
              saleId: this.currentBullSale.id
            }
          })
          .catch(() => {})
      } else {
        this.$router.push({ path: path }).catch(() => {})
      }
    },

    autoRefreshChanged () {
      if (this.isAutoRefresh) {
        this.setTimer()
      } else {
        this.clearTimer()
      }
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    this.clearTimer()
  }
}
</script>
